/**
 * Pluggable unauthorized-response handler.
 *
 * `rtkQueryErrorMiddleware` lives in this platform-agnostic store package, but
 * the *response* to a stale-session 401 (clear the session, evict cookies,
 * redirect to the login form) is app-specific — it needs browser navigation and
 * web-only knowledge of the host surface. Rather than pull that into the store,
 * the consuming app registers a handler here and the middleware invokes it.
 *
 * The web app registers a handler that funnels through
 * `apps/web/lib/auth/sessionSanitize.ts`. Mobile (or any consumer that doesn't
 * register one) simply gets the default no-op, preserving today's behavior.
 *
 * A handler returns `true` when it has taken ownership of the 401 (and will, for
 * example, redirect), so the middleware can suppress the generic
 * "session expired" notification toast for that response.
 */

export interface UnauthorizedContext {
  /** HTTP status of the rejected response (always 401 when invoked). */
  status: number;
  /** RTK Query endpoint name, when resolvable from the rejected action. */
  endpointName?: string;
}

export type UnauthorizedHandler = (context: UnauthorizedContext) => boolean | void;

let registeredHandler: UnauthorizedHandler | null = null;

/**
 * Register (or clear, by passing `null`) the global unauthorized handler.
 * Last registration wins; the app typically registers once at startup.
 */
export function setUnauthorizedHandler(handler: UnauthorizedHandler | null): void {
  registeredHandler = handler;
}

/** Read the currently-registered handler, or `null` if none. */
export function getUnauthorizedHandler(): UnauthorizedHandler | null {
  return registeredHandler;
}
